import { Asset } from "@constants/assets"
import {
  ExegesisAuthor,
  ExegesisChapterAsset,
  ExegesisMetadata,
  ExegesisRecord,
  ExegesisVerseContent,
} from "@constants/records/ExegesisRecord"
import { Locale } from "@constants/settings"
import { ThoughtSchool } from "@constants/ThoughtSchool"
import { repo } from "@db/repo"
import { unpackIPC } from "@services/Converter"
import {
  FingerprintedAsset,
  isAssetCurrent,
  saveFingerprints,
} from "@services/fingerprinter"
import LOGGER from "@services/Logger"
import { mergeKeys } from "@services/mutator"
import { pickLocalized } from "@services/picker"
import { create } from "zustand"

const useExegesisState = create<ExegesisState>((set, get) => ({
  exegesis: {},
  exegesisDetail: {},
  selectedExegesisId: null,

  setSelectedExegesisId(exegesisId) {
    set((s) => ({
      ...s,
      selectedExegesisId: exegesisId,
    }))
  },

  getVerseExegesis(exegesisId, chapterId, verseNumber) {
    return get().exegesis[exegesisId]?.[chapterId]?.[verseNumber] ?? null
  },

  async getExegesisDetail(exegesisId: string, locale: Locale) {
    set({ selectedExegesisId: exegesisId })

    const cached = get().exegesisDetail[exegesisId]
    if (cached) {
      return {
        name: cached.name,
        source: cached.source,
        longDescription:
          cached.longDescription[locale] ??
          cached.longDescription[Locale.IntEnglish] ??
          Object.values(cached.longDescription).find(Boolean) ??
          [],
        authors: cached.authors.map((a) => ({
          name: a.name,
          bio:
            a.bio[locale] ??
            a.bio[Locale.IntEnglish] ??
            Object.values(a.bio).find(Boolean) ??
            "",
        })),
      }
    }

    await ensureMetadata(exegesisId)

    const [record] = unpackIPC(
      await repo.exegesis.findAllBy({ id: exegesisId }),
    )
    if (!record)
      return { name: "", source: "", longDescription: [], authors: [] }

    const detail = {
      name: record.locNames as Partial<Record<Locale, string>>,
      longDescription: record.longDescription as Partial<
        Record<Locale, string[]>
      >,
      source: record.source,
      authors: record.authors as ExegesisAuthor[],
    }

    set((s) => ({
      exegesisDetail: { ...s.exegesisDetail, [exegesisId]: detail },
    }))

    return {
      name:
        detail.name[locale] ??
        detail.name[Locale.IntEnglish] ??
        Object.values(detail.name).find(Boolean) ??
        "",
      source: detail.source,
      longDescription:
        detail.longDescription[locale] ??
        detail.longDescription[Locale.IntEnglish] ??
        Object.values(detail.longDescription).find(Boolean) ??
        [],
      authors: detail.authors.map((a) => ({
        name: a.name,
        bio:
          a.bio[locale] ??
          a.bio[Locale.IntEnglish] ??
          Object.values(a.bio).find(Boolean) ??
          "",
      })),
    }
  },

  async getShortDesc(exegesisId, locale) {
    await ensureMetadata(exegesisId)
    const records = unpackIPC(await repo.exegesis.findAllBy({ id: exegesisId }))
    const record = records[0]
    if (!record) return ""
    const desc = record.description as Partial<Record<Locale, string>>
    return (
      desc[locale] ??
      desc[Locale.IntEnglish] ??
      Object.values(desc).find(Boolean) ??
      ""
    )
  },

  async loadChapter(exegesisId, chapterId) {
    const [, locale] = exegesisId.split("/")
    const source = Asset.exegesisOf(exegesisId)
    if (!source) throw new Error(`Unknown exegesis source: ${exegesisId}`)

    const chapterUrl = Asset.exegesisAssetUrlOf(
      exegesisId,
      locale as Locale,
      chapterId,
    )
    await ensureMetadata(exegesisId)

    const [exegesis] = unpackIPC(
      await repo.exegesis.findAllBy({ id: exegesisId }),
    )
    const isDownloaded =
      exegesis?.downloadedChapters.includes(chapterId) ?? false
    const isRecent = await isAssetCurrent(chapterUrl)

    let versesExegesis: Record<number, ExegesisVerseContent>

    if (isDownloaded && isRecent) {
      versesExegesis = unpackIPC(
        await repo.exegesisContent.findByChapter(exegesisId, chapterId),
      )
    } else {
      if (isDownloaded) {
        await repo.exegesis.unmarkChapter(exegesisId, chapterId)
        LOGGER.debug(
          `Exegesis fingerprint drifted, re-fetching: ${exegesisId} ch${chapterId}`,
        )
      }
      versesExegesis = await downloadChapter(exegesisId, chapterId)
      await repo.exegesis.markChapter(exegesisId, chapterId)
    }

    set((s) => ({
      exegesis: mergeKeys(s.exegesis, [exegesisId, chapterId], versesExegesis),
    }))
  },
}))

// Coalesces concurrent downloads for the same (exegesisId, chapterId) so
// only one deleteChapter + createBulk sequence ever runs at a time.
const activeDownloads = new Map<
  string,
  Promise<Record<number, ExegesisVerseContent>>
>()

function downloadChapter(
  exegesisId: string,
  chapterId: number,
): Promise<Record<number, ExegesisVerseContent>> {
  const key = `${exegesisId}:${chapterId}`
  const inflight = activeDownloads.get(key)
  if (inflight) return inflight

  const promise = doDownloadChapter(exegesisId, chapterId).finally(() => {
    activeDownloads.delete(key)
  })
  activeDownloads.set(key, promise)
  return promise
}

async function doDownloadChapter(
  exegesisId: string,
  chapterId: number,
): Promise<Record<number, ExegesisVerseContent>> {
  // Delete any stale rows before inserting fresh ones — guards against both
  // fingerprint-drift re-downloads and interrupted previous downloads where
  // markChapter never ran.
  await repo.exegesisContent.deleteChapter(exegesisId, chapterId)

  const [, locale] = exegesisId.split("/")
  const chapterUrl = Asset.exegesisAssetUrlOf(
    exegesisId,
    locale as Locale,
    chapterId,
  )
  const data =
    await FingerprintedAsset.readJson<ExegesisChapterAsset>(chapterUrl)

  const rows = Object.entries(data.translations).map(
    ([verseKey, translation]) => ({
      exegesisId,
      chapterId,
      verseNumber: Number(verseKey),
      translation,
      exegesis: data.exegesis?.[verseKey] ?? null,
      footnotes: data.footnotes?.[verseKey] ?? {},
    }),
  )

  // Chapter-level introductory discussion/description; 0 is used as sentinel/bait for such data
  if (data.description) {
    rows.push({
      exegesisId,
      chapterId,
      verseNumber: 0,
      translation: data.description,
      exegesis: null,
      footnotes: {},
    })
  }

  if (rows.length > 0) await repo.exegesisContent.createBulk(rows)

  saveFingerprints({ merge: true })

  return Object.fromEntries(
    rows.map((r) => [
      r.verseNumber,
      {
        translation: r.translation,
        exegesis: r.exegesis,
        footnotes: r.footnotes,
      },
    ]),
  )
}

async function ensureMetadata(exegesisId: string): Promise<void> {
  const existing = unpackIPC(await repo.exegesis.findAllBy({ id: exegesisId }))
  if (existing.length > 0) return

  const source = Asset.exegesisOf(exegesisId)
  if (!source) throw new Error(`Unknown exegesis source: ${exegesisId}`)

  const about = await FingerprintedAsset.readJson<ExegesisMetadata>(
    `${source.path}/about.json`,
  )

  // Check again after the async fetch — a concurrent call may have already
  // inserted this row while we were waiting for about.json.
  const afterFetch = unpackIPC(
    await repo.exegesis.findAllBy({ id: exegesisId }),
  )
  if (afterFetch.length > 0) return

  const authors: ExegesisAuthor[] = Object.entries(about.authors).map(
    ([name, { bio }]) => ({ name, bio }),
  )

  await repo.exegesis.create({
    id: exegesisId,
    oriName: about.name,
    locNames: pickLocalized(about.locNames ?? {}, (v) => v),
    description: pickLocalized(about.about ?? {}, (v) => v.shortDesc),
    longDescription: pickLocalized(about.about ?? {}, (v) => v.detailDesc),
    authors,
    thoughtSchool: ThoughtSchool.fromNameString(about.thought),
    source: about.source,
    downloadedChapters: [],
  })

  LOGGER.debug(`Stored exegesis metadata: ${exegesisId}`)
}

type VerseExegesis = Record<number, ExegesisVerseContent>
type ChapterExegesis = Record<number, VerseExegesis>

export interface ExegesisState {
  /** In-memory cache of verse content, keyed by exegesisId → chapterId → verseNumber. */
  exegesis: Record<string, ChapterExegesis>
  exegesisDetail: Record<string, ExegesisDetail>
  selectedExegesisId: string | null

  /**
   * Fetch an exegesis's detail (long description + source) for the given locale.
   * Long description falls back to English, then any available locale, then [].
   * Serves from cache on subsequent calls.
   */
  getExegesisDetail: (
    exegesisId: string,
    locale: Locale,
  ) => Promise<ExegesisDetail>

  setSelectedExegesisId: (exegesisId: string | null) => void

  /**
   * Fetch the short description for an exegesis source in the given locale,
   * before falling back to English/available locale or if neither exists: empty string
   */
  getShortDesc: (exegesisId: string, locale: Locale) => Promise<string>

  /** Return cached verse content for a specific verse, or null if not yet loaded. */
  getVerseExegesis: (
    exegesisId: string,
    chapterId: number,
    verseNumber: number,
  ) => ExegesisVerseContent | null

  /**
   * Ensure a chapter's verse content is available in the cache.
   * On first call: fetches the chapter JSON, stores rows in the DB, and
   * marks the chapter as downloaded. On subsequent calls: serves from the DB
   * unless the remote fingerprint has drifted, in which case stale rows are
   * replaced before re-populating the cache.
   */
  loadChapter: (exegesisId: string, chapterId: number) => Promise<void>
}

interface ExegesisDetail extends Pick<
  ExegesisRecord,
  "authors" | "longDescription" | "source"
> {
  name: ExegesisRecord["locNames"]
}

export default useExegesisState
